import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Leaf, FlaskConical, Sun, Sparkles, ArrowRight, Star, ShieldCheck, Truck, Award } from "lucide-react";
import { api, HERO_BOX_URL, HERO_BOX_ALT, currency } from "@/lib/api";
import { ProductCard, SectionHeading } from "@/components/Bits";
import { useLead } from "@/context/LeadDialog";

const PILLARS = [
  { icon: Leaf, title: "Natural", body: "Thoughtfully selected ingredients." },
  { icon: FlaskConical, title: "Responsible", body: "Quality-focused product development." },
  { icon: Sun, title: "Daily", body: "Designed to fit easily into everyday routines." },
  { icon: Sparkles, title: "Simple", body: "Nutrition made easy to understand and easy to use." },
];

export default function Home() {
  const [hero, setHero] = useState(null);
  const [products, setProducts] = useState([]);
  const { openLead } = useLead();

  useEffect(() => {
    (async () => {
      const [{ data: heroData }, { data: all }] = await Promise.all([
        api.get("/products?hero=true"),
        api.get("/products"),
      ]);
      setHero(heroData[0] || null);
      setProducts(all.filter((p) => !p.is_hero));
    })();
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-16">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5 fade-up">
              <div className="eyebrow">30 Days · Daily Nutrition</div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] mt-4 tracking-tight text-asure-text">
                Better <em className="not-italic text-asure-orange">every</em> day.
              </h1>
              <p className="mt-6 text-lg text-asure-text2 max-w-md leading-relaxed">
                Discover a smarter approach to everyday nutrition with thoughtfully selected ingredients and simple daily routines.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={() => openLead("Spirulina + Sea Buckthorn — 30 Day Box (Combo)")} className="btn-primary" data-testid="hero-enquire">I'm Interested <ArrowRight size={16} /></button>
                <Link to="/shop" className="btn-ghost" data-testid="hero-shop">Browse Product</Link>
              </div>
              <div className="mt-10 flex items-center gap-6 text-xs text-asure-text2 flex-wrap">
                <span className="flex items-center gap-1"><Star size={12} className="fill-asure-gold text-asure-gold" /> Premium quality</span>
                <span className="flex items-center gap-1"><Truck size={12} className="text-asure-forest" /> Pan-India delivery</span>
                <span className="flex items-center gap-1"><ShieldCheck size={12} className="text-asure-forest" /> Thoughtfully sourced</span>
              </div>
            </div>
            <div className="lg:col-span-7 relative">
              <div className="relative rounded-3xl overflow-hidden" style={{ background: "var(--asure-forest)" }}>
                <img src={HERO_BOX_URL} alt="ASURE Daily Nutrition 30-day box" className="w-full h-[560px] object-cover object-center" />
                <div className="absolute left-6 top-6 bg-white/95 backdrop-blur rounded-full px-4 py-2 text-xs font-bold tracking-wider text-asure-forest">SPIRULINA × SEA BUCKTHORN</div>
                <div className="absolute right-6 bottom-6 bg-asure-orange text-white rounded-2xl px-5 py-3 text-sm font-semibold shadow-lg">30 Days of Daily Nutrition · ₹899</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="section" style={{ background: "white" }}>
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <SectionHeading eyebrow="Why ASURE" title="Nutrition for your everyday" subtitle="Four principles that shape how we design every ASURE product." />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map((p, i) => (
              <div key={p.title} className="card-surface p-8 hover:-translate-y-1 transition-transform duration-500" data-testid={`pillar-${p.title.toLowerCase()}`}>
                <div className="inline-flex w-12 h-12 rounded-full items-center justify-center" style={{ background: "var(--asure-ivory)", color: "var(--asure-forest)" }}><p.icon size={20} /></div>
                <div className="font-display text-2xl mt-6">{p.title}</div>
                <p className="text-sm text-asure-text2 mt-2 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HERO PRODUCT FEATURED */}
      {hero && (
        <section className="section" style={{ background: "var(--asure-ivory)" }}>
          <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img src={HERO_BOX_ALT} alt="" className="w-full rounded-3xl" />
              <div className="absolute -top-6 -right-6 bg-white rounded-full px-5 py-3 shadow-md border border-[color:var(--asure-border)]"><span className="eyebrow">30 Days</span></div>
            </div>
            <div>
              <div className="eyebrow">Our Hero Product</div>
              <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">Two powerful natural ingredients.<br/>One simple daily ritual.</h2>
              <p className="mt-6 text-asure-text2 leading-relaxed">{hero.description}</p>
              <div className="mt-8 flex items-baseline gap-3">
                <div className="text-3xl font-semibold text-asure-forest">{currency(hero.price)}</div>
                <div className="text-lg line-through text-asure-text2/60">{currency(hero.mrp)}</div>
                <div className="text-sm font-bold text-asure-orange">Introductory offer</div>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={() => openLead(hero.name)} className="btn-primary" data-testid="hero-featured-cta">I'm Interested</button>
                <Link to="/ingredients" className="btn-ghost">Explore the Formula</Link>
              </div>
              <div className="mt-8 flex flex-wrap gap-2">
                {hero.highlights?.map((h) => <span key={h} className="text-[11px] font-semibold tracking-wide px-3 py-1 rounded-full border border-asure-forest/30 text-asure-forest">{h}</span>)}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SPIRULINA x SEA BUCKTHORN split */}
      <section className="section" style={{ background: "var(--asure-forest)", color: "white" }}>
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-center">
            <div className="eyebrow" style={{ color: "var(--asure-gold)" }}>Our Hero Combination</div>
            <h2 className="font-display text-4xl md:text-6xl mt-3">Spirulina <span style={{ color: "var(--asure-gold)" }}>×</span> Sea Buckthorn</h2>
            <p className="mt-4 text-white/80 max-w-2xl mx-auto">Two naturally sourced ingredients brought together for a simple daily nutrition routine.</p>
          </div>
          <div className="mt-14 grid md:grid-cols-2 gap-8">
            <SplitCard title="Spirulina" tag="Green Nutrition" img="https://images.unsplash.com/photo-1664956618403-175f565f18be?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
              body="A natural source of protein, vitamins, minerals and antioxidants." />
            <SplitCard title="Sea Buckthorn" tag="Orange Superberry" img="https://images.unsplash.com/photo-1761852952485-4824edeab1c5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
              body="Rich in natural antioxidants and Omega 3, 6, 7 & 9." />
          </div>
          <div className="mt-12 text-center">
            <button onClick={() => openLead("Spirulina + Sea Buckthorn — 30 Day Box (Combo)")} className="btn-white" data-testid="combo-enquire">Enquire about the combo</button>
          </div>
        </div>
      </section>

      {/* 30-DAY JOURNEY */}
      <section className="section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <SectionHeading eyebrow="30-Day Experience" title="Your 30-day ASURE journey" subtitle="A daily nutrition ritual that fits into your everyday routine." />
          <div className="mt-12 grid grid-cols-6 md:grid-cols-10 gap-2">
            {Array.from({ length: 30 }).map((_, i) => {
              const day = i + 1;
              const milestone = [1, 7, 15, 30].includes(day);
              return (
                <div key={day} className={`aspect-square rounded-lg flex items-center justify-center text-xs font-semibold border ${milestone ? 'bg-asure-forest text-white border-asure-forest' : 'bg-white text-asure-text2 border-[color:var(--asure-border)]'}`}>
                  {day.toString().padStart(2, '0')}
                </div>
              );
            })}
          </div>
          <div className="mt-8 grid md:grid-cols-4 gap-6">
            {[["01","Start"], ["07","Build the routine"], ["15","Stay consistent"], ["30","Complete the journey"]].map(([d, l]) => (
              <div key={d} className="p-6 card-surface"><div className="font-display text-3xl text-asure-forest">Day {d}</div><div className="mt-2 text-sm text-asure-text2">{l}.</div></div>
            ))}
          </div>
          <div className="mt-10 text-center"><button onClick={() => openLead("Spirulina + Sea Buckthorn — 30 Day Box (Combo)")} className="btn-primary" data-testid="start-journey">Start Your 30-Day Journey</button></div>
        </div>
      </section>

      {/* SHOP GRID */}
      <section className="section" style={{ background: "white" }}>
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <SectionHeading eyebrow="The ASURE Collection" title="Explore our product range" />
            <Link to="/shop" className="text-sm font-semibold text-asure-forest link-underline">View all →</Link>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {products.slice(0, 10).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="py-14" style={{ background: "var(--asure-ivory)" }}>
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[[Award,"Premium quality"],[Leaf,"100% Natural"],[ShieldCheck,"Batch tested"],[Truck,"Pan-India delivery"]].map(([I,l]) => (
            <div key={l} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center border border-asure-forest/30 text-asure-forest"><I size={16} /></div>
              <span className="text-sm font-semibold text-asure-text">{l}</span>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL */}
      <section className="section" style={{ background: "var(--asure-forest)", color: "white" }}>
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-display text-5xl md:text-7xl leading-none">Better every day.</h2>
          <p className="mt-6 text-white/85 text-lg">Your everyday choices shape your everyday life. ASURE is here to make better nutrition a little simpler.</p>
          <button onClick={() => openLead()} className="btn-white mt-8" data-testid="final-enquire">Start Your ASURE Journey</button>
        </div>
      </section>
    </div>
  );
}

function SplitCard({ title, tag, img, body }) {
  return (
    <div className="rounded-3xl overflow-hidden bg-white/5 border border-white/10">
      <img src={img} alt="" className="w-full h-64 object-cover" />
      <div className="p-8">
        <div className="eyebrow" style={{ color: "var(--asure-gold)" }}>{tag}</div>
        <div className="font-display text-3xl mt-2">{title}</div>
        <p className="mt-3 text-white/80 text-sm leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
